import { useCallback, useEffect, useMemo, useState } from 'react'
import { Platform, View } from 'react-native'
import { Control, FieldValues, Path, useController } from 'react-hook-form'

import { twMerge } from 'tailwind-merge'
import Typography from '@ui/typography'

import { getSchema } from '../components'

import PickerUI from './PickerUI'
import { useDebouncedCallback } from 'use-debounce'
import { makeZodNumber, NumberInputSchema } from '@ca/common'
import { usePrescriptionLabel } from 'hooks/useAttributeLabel'

interface Props<T extends FieldValues> {
  name: Path<T>
  control: Control<T>
}

type Options = {
  label: string
  value: string
}

// const defaultValue1 = (value: string, name: string) =>
//   value.split('.')[0] || '0'
// const defaultValue2 = (value: string, name: string) =>
//   value.split('.')[1] || '00'

const getDefaults = (schema: NumberInputSchema, value: string) => {
  const zodSchema = makeZodNumber(schema)
  const parsed = zodSchema.safeParse(value)
  if (parsed.success) {
    const [int, dec] = parsed.data?.toString().split('.') ?? []

    return [int ?? '0', dec?.padEnd(2, '0') ?? '00'] as const
  }
  if (schema.default != null) {
    const [int, dec] = schema.default.toString().split('.') ?? []

    return [int ?? '0', dec ?? '00'] as const
  }

  return ['0', '00'] as const
}

export default function PickerBody<T extends FieldValues>(props: Props<T>) {
  const { name, control } = props

  const [_side, key] = name.split('_')
  const label = usePrescriptionLabel(name)
  const schema = useMemo(
    () => getSchema(key) || ({ min: 0, max: 0, step: 1 } as NumberInputSchema),
    [key]
  )

  const step = schema.step_picker ?? schema.step

  const isDoubleDigit = step < 1

  const { field } = useController({
    name,
    control,
  })

  const [defaultValue1, defaultValue2] = useMemo(
    () => getDefaults(schema, field.value),
    [schema, field.value]
  )

  const [value1, setValue1] = useState<string>(defaultValue1)
  const [value2, setValue2] = useState<string>(defaultValue2)

  const valuesBeforeDot = useMemo(() => {
    const options: Options[] = []
    const hasNegative = schema.min < 0

    for (let i = schema.min; i <= schema.max; i++) {
      const value = i

      if (hasNegative && isDoubleDigit && value === 0) {
        options.push({ label: '-0', value: '-0' })
      }
      options.push({ label: value.toString(), value: value.toString() })
    }

    return options.reverse()
  }, [schema.max, schema.min, isDoubleDigit])

  const valuesAfterDot = useMemo(
    () =>
      Array.from({ length: 1 / step }, (_, i) => {
        const value = i * step
        return {
          label: value.toFixed(2).split('.')[1],
          value: value.toFixed(2).split('.')[1],
        }
      }).reverse(),
    [step]
  )

  const debouncedUpdate = useDebouncedCallback(
    (value: string) => {
      field.onChange(value)
    },
    100,
    { leading: true, trailing: true }
  )

  useEffect(() => {
    if (!field.value && schema.default) {
      field.onChange(schema.default)
    }
  }, [])

  const onChange1 = useCallback(
    (newVal1: string) => {
      setValue1(newVal1)
      debouncedUpdate(isDoubleDigit ? `${newVal1}.${value2}` : newVal1)
    },
    [value2, isDoubleDigit, debouncedUpdate]
  )

  const onChange2 = useCallback(
    (newVal2: string) => {
      setValue2(newVal2)
      debouncedUpdate(`${value1}.${newVal2}`)
    },
    [value1, debouncedUpdate]
  )

  return (
    <View
      className={twMerge(
        'flex w-full flex-col items-center rounded-2xl',
        'gap-0 px-16 pt-12'
      )}
    >
      <Typography
        variant="XLB"
        className="text-center uppercase tracking-normal"
      >
        {label}
      </Typography>
      <PickerUI
        value1={value1}
        value2={value2}
        max={schema.max}
        min={schema.min}
        onChange1={onChange1}
        onChange2={onChange2}
        options1={valuesBeforeDot}
        options2={valuesAfterDot}
        isDoubleDigit={isDoubleDigit}
        loopOptions1={key === 'axis'}
      />
    </View>
  )
}
